import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const INFO_PAGES = {
  about: {
    title: "About Dyntra Store",
    intro:
      "Dyntra Store is India's most trusted B2B dental ecommerce & services platform. We help dentists, clinics, and dental labs source the right supplies, hire the right talent, and grow the right way — all in one place.",
    sections: [
      {
        heading: "Our mission",
        body:
          "Empower every dental professional in India with fair prices, verified products, and modern tools so they can focus on patient care.",
      },
      {
        heading: "What we do",
        body:
          "We combine an ecommerce marketplace with an AI-powered assistant (Dr. Dyntra), a dental talent marketplace (Hire Hub), consulting services, and clinic setup tools (Dyntra Connect).",
      },
      {
        heading: "Why dentists choose Dyntra",
        body:
          "100% genuine products, fast pan-India delivery, transparent pricing, and dedicated account managers for clinics that buy in bulk.",
      },
    ],
  },
  careers: {
    title: "Careers at Dyntra",
    intro:
      "Help us build the operating system for Indian dentistry. We're hiring across engineering, design, category management, sales, and clinical partnerships.",
    sections: [
      {
        heading: "Open roles",
        body:
          "We're currently hiring Full-Stack Engineers, Product Designers, Category Managers (Endodontics / Orthodontics), and Field Sales Executives across Delhi, Mumbai, Bangalore, and Chennai.",
      },
      {
        heading: "How to apply",
        body:
          "Email your resume to careers@dyntra.store with the role and city in the subject line. We reply to every application within 5 working days.",
      },
    ],
  },
  blog: {
    title: "Dyntra Blog",
    intro:
      "Product reviews, clinical guides, business tips, and industry news for the modern Indian dental practice.",
    sections: [
      {
        heading: "Coming soon",
        body:
          "Our editorial team is publishing the first wave of articles right now — subscribe to the newsletter in the footer to be notified when the blog goes live.",
      },
    ],
  },
  contact: {
    title: "Contact Us",
    intro:
      "We're here to help — whether it's a bulk quote, an order issue, or a product question.",
    sections: [
      {
        heading: "Email",
        body: "support@dyntra.store",
      },
      {
        heading: "Phone / WhatsApp",
        body: "+91 90000 00000 (Mon–Sat, 9:30 AM – 7:00 PM IST)",
      },
      {
        heading: "Registered address",
        body: "Dyntra Store, New Delhi 110001, India.",
      },
    ],
  },
  faqs: {
    title: "Frequently Asked Questions",
    intro:
      "Answers to the questions our customers ask most often.",
    sections: [
      {
        heading: "How fast is delivery?",
        body:
          "Free 3–5 day delivery across India for standard orders. Same-city same-day is available in Delhi NCR, Mumbai, and Bangalore.",
      },
      {
        heading: "Are your products genuine?",
        body:
          "Every listing is 100% genuine, sourced directly from the brand or an authorised distributor. Bills carry manufacturer batch numbers.",
      },
      {
        heading: "Do you offer GST invoicing?",
        body:
          "Yes. Add your clinic's GSTIN at checkout to receive a compliant tax invoice.",
      },
      {
        heading: "How do returns work?",
        body:
          "See our Return Policy — most items are eligible for a free 7-day return if unused and in original packaging.",
      },
    ],
  },
  "policy/shipping": {
    title: "Shipping Policy",
    intro:
      "Delivery timelines, charges, and coverage for every Dyntra Store order.",
    sections: [
      {
        heading: "Standard delivery",
        body:
          "Free 3–5 business day delivery for orders above ₹500. Below that, a flat ₹49 shipping fee applies. Cash on Delivery is available up to ₹10,000.",
      },
      {
        heading: "Serviceable pincodes",
        body:
          "We deliver to 26,000+ pincodes across India. Use the 'Deliver to' pill in the header to check serviceability for your clinic.",
      },
      {
        heading: "Damaged in transit",
        body:
          "Report any damage within 48 hours of delivery via support@dyntra.store — we'll arrange a free replacement.",
      },
    ],
  },
  "policy/return": {
    title: "Return Policy",
    intro:
      "Our return promise: if a product isn't right for you, we make it right.",
    sections: [
      {
        heading: "7-day return window",
        body:
          "You may return most unused items in original packaging within 7 days of delivery for a full refund.",
      },
      {
        heading: "Non-returnable items",
        body:
          "Custom-manufactured items, opened sterile packaging, prescription drugs, and hazardous chemicals cannot be returned once delivered.",
      },
      {
        heading: "Refund timeline",
        body:
          "Once the item reaches our warehouse and passes QC, refunds hit your account in 5–7 business days.",
      },
    ],
  },
  "policy/privacy": {
    title: "Privacy Policy",
    intro:
      "How Dyntra Store collects, uses, and protects your personal and clinic data.",
    sections: [
      {
        heading: "What we collect",
        body:
          "Only what we need to fulfil your orders and provide our services — name, contact details, delivery address, GSTIN, and order history.",
      },
      {
        heading: "How we use it",
        body:
          "To process orders, provide customer support, prevent fraud, and (with your consent) send you marketing updates. We never sell your data.",
      },
      {
        heading: "Your rights",
        body:
          "You can request a copy of your data or ask us to delete your account at any time by emailing privacy@dyntra.store.",
      },
    ],
  },
  "policy/terms": {
    title: "Terms & Conditions",
    intro:
      "These terms govern your use of Dyntra Store's website, mobile apps, and services.",
    sections: [
      {
        heading: "Accounts",
        body:
          "You must be 18+ and authorised to purchase dental supplies for a registered clinic, lab, or hospital.",
      },
      {
        heading: "Pricing & availability",
        body:
          "Prices, taxes, and stock are subject to change. We reserve the right to cancel any order that violates fair-use or resale policies.",
      },
      {
        heading: "Governing law",
        body:
          "These terms are governed by the laws of India and any disputes will be subject to the exclusive jurisdiction of the courts in New Delhi.",
      },
    ],
  },
  sitemap: {
    title: "Sitemap",
    intro: "Every top-level page on Dyntra Store.",
    sections: [
      {
        heading: "Shop",
        body: "Browse products, refurbished equipment, offers of the day.",
      },
      {
        heading: "Services",
        body: "Find Me Consultant, Hire Hub, Dr. Dyntra, Dyntra Connect.",
      },
      {
        heading: "Company",
        body: "About, Careers, Blog, Events, Contact, Help Center, FAQs.",
      },
      {
        heading: "Policies",
        body: "Shipping, Return, Privacy, Terms & Conditions.",
      },
    ],
  },
};

export default function InfoPage({ slug }) {
  const page = INFO_PAGES[slug] || {
    title: "Page Not Found",
    intro: "The page you're looking for doesn't exist yet.",
    sections: [],
  };

  return (
    <main className="dyntra-container py-12 sm:py-16 max-w-4xl">
      <nav className="flex items-center gap-1 text-xs text-gray-500 mb-4">
        <Link to="/" className="hover:text-[#4B2AAD]">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-gray-700 font-semibold">{page.title}</span>
      </nav>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900">{page.title}</h1>
      <p className="mt-3 text-gray-600 text-base sm:text-lg leading-relaxed">{page.intro}</p>

      <div className="mt-10 space-y-8">
        {page.sections.map((s) => (
          <section key={s.heading} className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <h2 className="font-display text-lg font-bold text-gray-900">{s.heading}</h2>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.body}</p>
          </section>
        ))}
      </div>

      <div className="mt-10 rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-5 flex items-center justify-between gap-4">
        <div>
          <p className="font-display text-base font-bold text-gray-900">Need more help?</p>
          <p className="text-sm text-gray-600 mt-0.5">Our support team is one tap away — reach out any time.</p>
        </div>
        <Link to="/support" className="dyntra-btn-primary shrink-0">Get support</Link>
      </div>
    </main>
  );
}
